import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Overview from './components/Overview';
import GarageVehicles from './components/GarageVehicles';
import Report from './components/Report';
import Calendar from './components/Calendar';
import Invoices from './components/Invoices';
import Archive from './components/Archive';
import Sales from './components/Sales';
import SalesReport from './components/SalesReport';
import GarageReport from './components/GarageReport';
import Links from './components/Links';
import Finance from './components/Finance';
import PaymentPage from './components/PaymentPage';
import Promotions from './components/Promotions';
import PartsLookup from './components/PartsLookup';
import TestAdmin from './components/TestAdmin';
import TechnicianDispatch from './components/TechnicianDispatch';
import Customers from './components/Customers';
import SettingsModal from './components/SettingsModal';
import BookingModal from './components/BookingModal';
import InvoiceModal from './components/InvoiceModal';
import GlobalSearch from './components/GlobalSearch';
import DataLoadErrorBar from './components/DataLoadErrorBar';
import DataLoadingBar from './components/DataLoadingBar';
import VehicleLimitBanner from './components/VehicleLimitBanner';
import ChatSupport from './components/ChatSupport';
import { MobileNavEnhancements, TouchGestureHandler, MobilePerformanceMonitor } from './components/MobileOptimization';
import './firebase/config'; // Initialize Firebase
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { ensureUserDoc } from './services/firestoreService';
import { FirebaseProvider } from './context/FirebaseContext';
import { AppProvider, useApp } from './context/AppContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import { useUserRole } from './hooks/useUserRole';
import { hasPageAccess } from './utils/rolePermissions';
import { isStageEnabled, hasGarageLanesEnabled } from './utils/enabledStages';
import { applyBrandingFromStorage } from './utils/brandingApply';
import { setCompanyFavicon } from './utils/favicon';
import { logoStorageService } from './services/storageService';
import './utils/removeDemoData'; // Make removeDemoData available globally
import './utils/generateDemoCars'; // Make generateDemoCars available globally
import './utils/removeSalesVehicles'; // Make removeSalesVehicles available globally
import { ToastProvider } from './context/ToastContext';
import './App.css';
import './styles/mobile-optimization.css';

const VehicleDetailModal = lazy(() => import('./components/VehicleDetailModal'));

function SalesStageGuard({ currentPage, onRedirect }) {
  const { garageInfo } = useApp();
  useEffect(() => {
    if ((currentPage === 'sales' || currentPage === 'sales-report') && !isStageEnabled(garageInfo, 'Sales')) {
      onRedirect('overview');
      sessionStorage.setItem('currentPage', 'overview');
    }
    if (
      (currentPage === 'garage-report' || currentPage === 'parts-lookup' || currentPage === 'technician-dispatch') &&
      !hasGarageLanesEnabled(garageInfo)
    ) {
      onRedirect('overview');
      sessionStorage.setItem('currentPage', 'overview');
    }
  }, [currentPage, garageInfo, onRedirect]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true); // Track if we're still checking auth
  // Restore current page from sessionStorage on mount
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem('currentPage');
    return savedPage || 'overview';
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(null);
  const [recentVehicleIndices, setRecentVehicleIndices] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [garageVehiclesFilter, setGarageVehiclesFilter] = useState('all');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const { role, permissions } = useUserRole();
  const vehiclesCountRef = useRef(0);

  // Global keyboard shortcuts (only when authenticated and not typing in inputs)
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleKeyDown = (e) => {
      const isInput = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) || e.target?.isContentEditable;
      if (isInput) return;
      const mod = e.ctrlKey || e.metaKey;
      // Ctrl/Cmd + K: open global search
      if (mod && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        return;
      }
      // Ctrl/Cmd + B: New booking
      if (mod && e.key === 'b') {
        e.preventDefault();
        setIsBookingOpen(true);
        return;
      }
      // Ctrl/Cmd + S: Save (dispatched to open modal)
      if (mod && e.key === 's') {
        if (isBookingOpen || selectedVehicleIndex !== null) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('app-save'));
        }
        return;
      }
      // Next/Previous vehicle when vehicle detail is open (no modifier)
      if (!mod && selectedVehicleIndex !== null) {
        const count = vehiclesCountRef.current || 0;
        if (e.key === 'ArrowRight' && count > 0) {
          e.preventDefault();
          setSelectedVehicleIndex((prev) => (prev === null ? null : Math.min(prev + 1, count - 1)));
        } else if (e.key === 'ArrowLeft' && count > 0) {
          e.preventDefault();
          setSelectedVehicleIndex((prev) => (prev === null ? null : Math.max(0, prev - 1)));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAuthenticated, isBookingOpen, selectedVehicleIndex]);

  const handleOpenVehicleDetail = useCallback((index) => {
    setIsSidebarOpen(false);
    setSelectedVehicleIndex(index);
    setRecentVehicleIndices((prev) => {
      const next = [index, ...prev.filter((i) => i !== index)];
      return next.slice(0, 5);
    });
  }, []);

  const handleCloseVehicleDetail = useCallback(() => {
    document.body.style.overflow = '';
    setSelectedVehicleIndex(null);
  }, []);

  const handleNavigate = useCallback((page) => {
    setCurrentPage(page);
    setIsSidebarOpen(false);
  }, []);

  const handleCloseSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true);
    setIsSidebarOpen(false);
  }, []);

  const handleOpenChat = useCallback(() => {
    setIsChatOpen(true);
    setIsSidebarOpen(false);
  }, []);

  const handleBookVehicle = useCallback(() => {
    setIsSidebarOpen(false);
    setIsBookingOpen(true);
  }, []);

  const handleMenuClick = useCallback(() => setIsSidebarOpen(prev => !prev), []);

  const handleOpenSearch = useCallback(() => {
    setIsSidebarOpen(false);
    setIsSearchOpen(true);
  }, []);

  const handleUpgrade = useCallback(() => {
    setCurrentPage('payment');
    sessionStorage.setItem('currentPage', 'payment');
  }, []);

  const handleCloseSettings = useCallback(() => setIsSettingsOpen(false), []);
  const handleCloseBooking = useCallback(() => setIsBookingOpen(false), []);
  const handleCloseSearch = useCallback(() => setIsSearchOpen(false), []);
  const handleCloseChat = useCallback(() => setIsChatOpen(false), []);

  const handleOpenBooking = useCallback(() => setIsBookingOpen(true), []);

  const handleSwipeRight = useCallback(() => setIsSidebarOpen(true), []);
  const handleSwipeLeft = useCallback(() => setIsSidebarOpen(false), []);

  const handleNavigateToGarageVehicles = useCallback((statusFilter = 'all') => {
    setGarageVehiclesFilter(statusFilter);
    setCurrentPage('garagevehicles');
  }, []);

  const handleNavigateToCalendar = useCallback(() => setCurrentPage('calendar'), []);
  const handleNavigateToFinance = useCallback(() => setCurrentPage('finance'), []);
  const handleNavigateToSales = useCallback(() => setCurrentPage('sales'), []);
  const handleNavigateToGarageReport = useCallback(() => setCurrentPage('garage-report'), []);

  const handleDisplayInvoice = useCallback((invoice) => {
    setIsSidebarOpen(false);
    setSelectedInvoice(invoice);
    document.body.style.overflow = 'hidden';
  }, []);

  const handleCloseInvoice = useCallback(() => {
    setSelectedInvoice(null);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    let unsubscribeAuth = null;
    
    // Check authentication from sessionStorage
    const sessionAuth = sessionStorage.getItem('authenticated') === 'true';
    const authType = sessionStorage.getItem('authType');
    
    // If user is authenticated via Firebase, verify auth state
    if (sessionAuth && authType === 'firebase') {
      // onAuthStateChanged fires immediately with current user, then on changes
      // This prevents the login flash by checking auth state before rendering
      unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        if (user) {
          // Ensure Firestore user doc exists so this user can see company data (vehicles, invoices)
          try {
            await ensureUserDoc(user.uid, {
              email: user.email || '',
              username: user.email ? user.email.split('@')[0] : '',
            });
          } catch (err) {
            console.error('ensureUserDoc on auth state:', err);
          }
          setIsAuthenticated(true);
        } else {
          // User is no longer authenticated in Firebase, clear session
          sessionStorage.removeItem('authenticated');
          sessionStorage.removeItem('username');
          sessionStorage.removeItem('authType');
          sessionStorage.removeItem('userId');
          sessionStorage.removeItem('userEmail');
          sessionStorage.removeItem('currentPage');
          setIsAuthenticated(false);
        }
        // Auth check is complete after first callback
        setIsAuthChecking(false);
      });
    } else if (sessionAuth) {
      // Admin or other non-Firebase authentication
      setIsAuthenticated(true);
      setIsAuthChecking(false);
    } else {
      // No session auth found, still need to check Firebase in case session expired
      // But user might still be authenticated in Firebase
      unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        if (user) {
          // Ensure Firestore user doc exists so this user can see company data (vehicles, invoices)
          try {
            await ensureUserDoc(user.uid, {
              email: user.email || '',
              username: user.email ? user.email.split('@')[0] : '',
            });
          } catch (err) {
            console.error('ensureUserDoc on auth state:', err);
          }
          // Restore session info
          sessionStorage.setItem('authenticated', 'true');
          sessionStorage.setItem('authType', 'firebase');
          sessionStorage.setItem('userId', user.uid);
          if (user.email) {
            sessionStorage.setItem('userEmail', user.email);
          }
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
        // Auth check is complete after first callback
        setIsAuthChecking(false);
      });
    }

    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
    localStorage.setItem('theme', savedTheme);

    // Viewport height fix for mobile browsers
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    // Load branding colors (contrast-safe sidebar + main text)
    applyBrandingFromStorage();

    // Use company logo as favicon (login + authenticated)
    const loadFavicon = async () => {
      try {
        const url = await logoStorageService.getLogoURL();
        if (url) setCompanyFavicon(url);
      } catch (error) {
        console.warn('Could not load company logo for favicon:', error);
      }
    };
    loadFavicon();

    requestAnimationFrame(() => {
      applyBrandingFromStorage();
    });

    const handleBrandingUpdate = (e) => {
      applyBrandingFromStorage();
      if (e?.detail?.logo) {
        setCompanyFavicon(e.detail.logo);
      }
    };

    window.addEventListener('brandingUpdated', handleBrandingUpdate);

    return () => {
      // Cleanup auth listener if it was set up
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Save current page to sessionStorage whenever it changes
  useEffect(() => {
    if (isAuthenticated && currentPage) {
      sessionStorage.setItem('currentPage', currentPage);
    }
  }, [currentPage, isAuthenticated]);

  // Close all modals when navigating to a different page
  useEffect(() => {
    if (isAuthenticated) {
      // Close all modals and restore body scroll
      document.body.style.overflow = '';
      setIsSettingsOpen(false);
      setIsBookingOpen(false);
      setSelectedVehicleIndex(null);
      setIsChatOpen(false);
      setSelectedInvoice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isAuthenticated]);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    const authType = sessionStorage.getItem('authType');
    
    // If user is logged in via Firebase, sign them out
    if (authType === 'firebase') {
      try {
        await signOut(auth);
      } catch (error) {
        console.error('Firebase sign out error:', error);
      }
    }
    
    // Clear all session storage
    sessionStorage.removeItem('authenticated');
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('authType');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userEmail');
    sessionStorage.removeItem('currentPage');
    
    setIsAuthenticated(false);
  };

  // Check if user has access to current page, redirect if not
  // This must be called before any conditional returns (React Hooks rule)
  useEffect(() => {
    if (isAuthenticated && role && permissions) {
      if (!hasPageAccess(role, permissions, currentPage)) {
        // User doesn't have access to current page, redirect to overview
        console.warn(`User with role "${role}" does not have access to page "${currentPage}". Redirecting to overview.`);
        setCurrentPage('overview');
        sessionStorage.setItem('currentPage', 'overview');
      }
    }
  }, [isAuthenticated, role, permissions, currentPage]);

  // Show loading state while checking authentication
  // This prevents the login page from flashing on page refresh
  if (isAuthChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#faf9f7'
      }}>
        {/* Optional: Add a loading spinner or logo here */}
      </div>
    );
  }

  // Only show login page after auth check is complete and user is not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const renderPage = () => {
    // Check if user has access to the requested page
    if (!hasPageAccess(role, permissions, currentPage)) {
      // If no access, show overview instead
      return <Overview onOpenVehicleDetail={handleOpenVehicleDetail} onNavigateToGarageVehicles={handleNavigateToGarageVehicles} onNavigateToGarageReport={handleNavigateToGarageReport} onNavigateToCalendar={handleNavigateToCalendar} onNavigateToFinance={handleNavigateToFinance} onNavigateToSales={handleNavigateToSales} onOpenBooking={handleOpenBooking} currentPage={currentPage} recentVehicleIndices={recentVehicleIndices} />;
    }

    switch (currentPage) {
      case 'overview':
        return <Overview onOpenVehicleDetail={handleOpenVehicleDetail} onNavigateToGarageVehicles={handleNavigateToGarageVehicles} onNavigateToGarageReport={handleNavigateToGarageReport} onNavigateToCalendar={handleNavigateToCalendar} onNavigateToFinance={handleNavigateToFinance} onNavigateToSales={handleNavigateToSales} onOpenBooking={handleOpenBooking} currentPage={currentPage} recentVehicleIndices={recentVehicleIndices} />;
      case 'garage-report':
        return <GarageReport onOpenVehicleDetail={handleOpenVehicleDetail} />;
      case 'garagevehicles':
        return <GarageVehicles onOpenVehicleDetail={handleOpenVehicleDetail} initialStatusFilter={garageVehiclesFilter} />;
      case 'report':
        return <Report onOpenVehicleDetail={handleOpenVehicleDetail} onDisplayInvoice={handleDisplayInvoice} />;
      case 'calendar':
        return <Calendar onOpenVehicleDetail={handleOpenVehicleDetail} onBookVehicle={handleOpenBooking} currentPage={currentPage} />;
      case 'invoices':
        return <Invoices onDisplayInvoice={handleDisplayInvoice} />;
      case 'archive':
        return <Archive onOpenVehicleDetail={handleOpenVehicleDetail} />;
      case 'sales':
        return <Sales onOpenVehicleDetail={handleOpenVehicleDetail} />;
      case 'sales-report':
        return <SalesReport onOpenVehicleDetail={handleOpenVehicleDetail} />;
      case 'links':
        return <Links />;
      case 'promotions':
        return <Promotions />;
      case 'parts-lookup':
        return (
          <div className="vehicle-inner">
            <div className="vehicle-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0 }}>Parts Lookup</h2>
              <p className="vehicle-subtitle" style={{ margin: 0, width: '100%' }}>Search parts by vehicle or description</p>
            </div>
            <PartsLookup embedded vehicle={null} onClose={() => setCurrentPage('garagevehicles')} />
          </div>
        );
      case 'finance':
        return <Finance onOpenVehicleDetail={handleOpenVehicleDetail} />;
      case 'payment':
        return <PaymentPage />;
      case 'test-admin':
        return <TestAdmin />;
      case 'technician-dispatch':
        return <TechnicianDispatch onOpenVehicleDetail={handleOpenVehicleDetail} />;
      case 'customers':
        return <Customers onOpenVehicleDetail={handleOpenVehicleDetail} />;
      default:
        return <Overview onOpenVehicleDetail={handleOpenVehicleDetail} onNavigateToGarageVehicles={handleNavigateToGarageVehicles} onNavigateToGarageReport={handleNavigateToGarageReport} onNavigateToCalendar={handleNavigateToCalendar} onNavigateToFinance={handleNavigateToFinance} onNavigateToSales={handleNavigateToSales} onOpenBooking={handleOpenBooking} currentPage={currentPage} recentVehicleIndices={recentVehicleIndices} />;
    }
  };

  return (
    <FirebaseProvider>
      <FeatureFlagsProvider>
      <SubscriptionProvider>
        <AppProvider vehiclesCountRef={vehiclesCountRef}>
          <ToastProvider>
            <SalesStageGuard currentPage={currentPage} onRedirect={handleNavigate} />
          <MobilePerformanceMonitor>
          <TouchGestureHandler 
            onSwipeRight={handleSwipeRight}
            onSwipeLeft={handleSwipeLeft}
          >
            <div className="app-container" data-responsive="true">
              <div 
                className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
                onClick={handleCloseSidebar}
                aria-hidden="true"
                role="presentation"
              ></div>
              <Sidebar 
                currentPage={currentPage} 
                onNavigate={handleNavigate}
                onLogout={handleLogout}
                onOpenSettings={handleOpenSettings}
                onOpenChat={handleOpenChat}
                isOpen={isSidebarOpen}
                onClose={handleCloseSidebar}
              />
              <main className="main-content">
                <MobileNavEnhancements>
                  <Header 
                    onBookVehicle={handleBookVehicle}
                    onMenuClick={handleMenuClick}
                    onOpenSearch={handleOpenSearch}
                    currentPage={currentPage}
                  />
                </MobileNavEnhancements>
                <DataLoadingBar />
                <DataLoadErrorBar />
                <VehicleLimitBanner onUpgrade={handleUpgrade} />
                <div className="app-shell">
                  <div className={`page-content active`} id={`${currentPage}Page`}>
                    {renderPage()}
                  </div>
                </div>
              </main>
              {isSettingsOpen && (
                <SettingsModal onClose={handleCloseSettings} />
              )}
              {isBookingOpen && (
                <BookingModal onClose={handleCloseBooking} />
              )}
              {selectedVehicleIndex !== null && (
                <Suspense fallback={null}>
                  <VehicleDetailModal 
                    vehicleIndex={selectedVehicleIndex} 
                    onClose={handleCloseVehicleDetail} 
                  />
                </Suspense>
              )}
              <GlobalSearch
                isOpen={isSearchOpen}
                onClose={handleCloseSearch}
                onOpenVehicle={handleOpenVehicleDetail}
                onOpenInvoice={handleDisplayInvoice}
              />
              <ChatSupport isOpen={isChatOpen} onClose={handleCloseChat} />
              {selectedInvoice && (
                <InvoiceModal 
                  invoice={selectedInvoice}
                  onClose={handleCloseInvoice}
                />
              )}
            </div>
          </TouchGestureHandler>
        </MobilePerformanceMonitor>
          </ToastProvider>
      </AppProvider>
    </SubscriptionProvider>
    </FeatureFlagsProvider>
    </FirebaseProvider>
  );
}

export default App;


